// #3647 (station-monitor workstream) -- the ONE worst exception a station has,
// for its row in the dashboard's Scribe Station Status table (Jaybill,
// 2026-10-22: a station's exception belongs on that station's row, one short
// line, and a station with several shows only the worst).
//
// Pure and display-free: returns a code plus the numbers the line needs, never
// text (the component owns the static i18n keys). It reads the SAME server row
// and the SAME classifiers the dashboard banners use (findStuckStations,
// findSilentStations, findRingMovedOnSheets), so a row and a banner can never
// disagree about whether a station has a problem.
//
// Worst first. Stuck and silent are the stranded-score cases (red); a started
// sheet the ring has moved past is next; then sheets needing attention, then
// merely queued sheets, then an old build, then a live station that never
// uploaded.
import { findRingMovedOnSheets, findSilentStations, findStuckStations } from "./stationMonitor";
import { elapsedMsSince } from "./scribePresence";

export const STATION_ROW_EXCEPTION_ORDER = Object.freeze([
  "stuck",
  "silent",
  "ringMovedOn",
  "needsAttention",
  "queued",
  "oldBuild",
  "neverUploaded",
]);

// { code, severity: "error" | "warning", count?, ageMs?, silentMs?, unsent? } or null.
export function worstStationException(row, now = Date.now()) {
  if (!row) return null;

  const stuck = findStuckStations([row], now)[0];
  if (stuck) return { code: "stuck", severity: "error", ageMs: stuck.ageMs };

  const silent = findSilentStations([row], now)[0];
  if (silent) return { code: "silent", severity: "error", silentMs: silent.silentMs, unsent: silent.unsent };

  const movedOn = findRingMovedOnSheets([row]);
  if (movedOn.length > 0) return { code: "ringMovedOn", severity: "warning", count: movedOn.length };

  if ((row.needsAttention ?? 0) > 0) return { code: "needsAttention", severity: "warning", count: row.needsAttention };

  if ((row.queued ?? 0) > 0) {
    return {
      code: "queued",
      severity: "warning",
      count: row.queued,
      ageMs: row.oldestUnshippedAt ? elapsedMsSince(row.oldestUnshippedAt, now) : null,
    };
  }

  if (row.live === true && row.oldBuild === true) return { code: "oldBuild", severity: "warning" };

  if (row.live === true && (row.snapshots ?? []).length === 0) return { code: "neverUploaded", severity: "warning" };

  return null;
}
